import { z } from 'zod';
import { idempotencyKeySchema } from './common.js';
/**
 * File-size and mime-type policy for POD uploads (Phase 4.75a):
 *   - Per-file cap:       10 MB  (POD_MAX_BYTES_PER_FILE)
 *   - Per-load cap:   25 MB  (POD_MAX_BYTES_PER_LOAD)
 *   - Mime whitelist:     image/jpeg|png|heic|webp + application/pdf
 *
 * The per-load cap is enforced in the service layer (queries the repo
 * for total bytes and adds the new size). The per-file cap and mime
 * whitelist are enforced here as schema validation.
 */
export const POD_MAX_BYTES_PER_FILE = 10 * 1024 * 1024;
export const POD_MAX_BYTES_PER_LOAD = 25 * 1024 * 1024;
export const POD_ALLOWED_MIME = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
    'application/pdf',
];
export const podKindValues = ['signature', 'photo', 'barcode', 'note'];
export const podKindSchema = z.enum(podKindValues);
const latitudeSchema = z.number().min(-90).max(90);
const longitudeSchema = z.number().min(-180).max(180);
const podMimeSchema = z
    .enum(POD_ALLOWED_MIME)
    .describe('Mime type — restricted to the POD allowed-list');
const podSizeSchema = z
    .number()
    .int()
    .positive()
    .max(POD_MAX_BYTES_PER_FILE, `File exceeds ${POD_MAX_BYTES_PER_FILE} byte limit`);
const noteTextSchema = z.string().trim().min(1).max(2000);
const signerNameSchema = z.string().trim().min(1).max(120);
const barcodeValueSchema = z.string().trim().min(1).max(255);
/**
 * POST /v1/fleets/:fleetId/loads/:loadId/pod/upload-init
 *
 * Body shape varies by kind:
 *  - signature/photo: must supply mimeType + sizeBytes; r2_key is generated
 *  - barcode: must supply barcodeValue; no file uploaded, status goes
 *             straight to 'captured' on confirm
 *  - note: must supply noteText; same as barcode — no file
 *
 * The server creates a `load_pods` row in 'pending' state and (for
 * file kinds) returns a presigned PUT URL. The client uploads, then calls
 * /confirm to finalise.
 */
export const podUploadInitSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('signature'),
        mimeType: podMimeSchema,
        sizeBytes: podSizeSchema,
        loadStopId: z.string().length(26).optional(),
        idempotencyKey: idempotencyKeySchema.optional(),
        signerName: signerNameSchema.optional(),
        noteText: noteTextSchema.optional(),
    }),
    z.object({
        kind: z.literal('photo'),
        mimeType: podMimeSchema,
        sizeBytes: podSizeSchema,
        loadStopId: z.string().length(26).optional(),
        idempotencyKey: idempotencyKeySchema.optional(),
        noteText: noteTextSchema.optional(),
    }),
    z.object({
        kind: z.literal('barcode'),
        barcodeValue: barcodeValueSchema,
        loadStopId: z.string().length(26).optional(),
        idempotencyKey: idempotencyKeySchema.optional(),
        noteText: noteTextSchema.optional(),
    }),
    z.object({
        kind: z.literal('note'),
        noteText: noteTextSchema,
        loadStopId: z.string().length(26).optional(),
        idempotencyKey: idempotencyKeySchema.optional(),
    }),
]);
/**
 * POST /v1/fleets/:fleetId/loads/:loadId/pod/:podId/confirm
 *
 * For file kinds: API verifies the upload (HEAD on R2), flips status to
 * 'captured'. For barcode/note kinds: this is the only call needed beyond
 * upload-init — the row was already populated.
 *
 * `transition` defaults to true. When the load's status is 'delivered'
 * AND no prior captured PODs exist, this auto-moves the load to
 * 'pod_captured'. Pass `transition: false` to suppress for supplementary
 * PODs on already-pod_captured loads.
 */
export const podConfirmSchema = z.object({
    signerName: signerNameSchema.optional(),
    noteText: noteTextSchema.optional(),
    captureLat: latitudeSchema.optional(),
    captureLng: longitudeSchema.optional(),
    transition: z.boolean().default(true),
});
export const podIdParamSchema = z.object({
    podId: z.string().length(26),
});
//# sourceMappingURL=pod.js.map